import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { usuariosApi, auditApi, entidadesApi, avisosApi, tareasApi, dashboardOperativoApi } from '../services/api';
import {
  Users, Briefcase, FileText, Sparkles, Shield, CheckCircle, Clock, ScrollText, ArrowRight,
  AlertTriangle, Bell, ListChecks, CheckSquare, Pin, Inbox, UserCog,
} from 'lucide-react';
import { formatFecha } from '../utils/dateFormat';

import WidgetCard from '../components/dashboard/WidgetCard';
import AvisoCard from '../components/dashboard/AvisoCard';
import DocsInternos from '../components/dashboard/DocsInternos';
import { CentroInteligenciaCard, QuickStat } from '../components/dashboard/Cards';

const ROL_LABEL = {
  admin: 'Administrador',
  dir_comercial: 'Director Comercial',
  comercial: 'Agente Comercial',
};

// ── Hooks de datos ───────────────────────────────────────────────────
const useDashboardData = (isAdmin) => {
  const [stats, setStats] = useState({ usuarios: null, logs: null, entidades: null });
  const [avisos, setAvisos] = useState([]);
  const [loadingAvisos, setLoadingAvisos] = useState(true);
  const [misTareas, setMisTareas] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(true);
  const [prospRes, setProspRes] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [loadingWidgets, setLoadingWidgets] = useState(true);
  const [pendientesRev, setPendientesRev] = useState(null);
  const [kpisOp, setKpisOp] = useState(null);
  const [actividadReciente, setActividadReciente] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const ents = await entidadesApi.list({ limit: 1000 }).catch(() => []);
        if (isAdmin) {
          const [users, logsCount] = await Promise.all([
            usuariosApi.list().catch(() => []),
            auditApi.count().catch(() => ({ total: 0 })),
          ]);
          setStats({ usuarios: users.length, logs: logsCount.total, entidades: ents.length });
        } else {
          setStats({ usuarios: null, logs: null, entidades: ents.length });
        }
      } catch (_) { /* noop */ }
    })();
    (async () => {
      try { setAvisos(await avisosApi.proximos(90)); }
      catch (_) { setAvisos([]); }
      finally { setLoadingAvisos(false); }
    })();
    (async () => {
      try {
        const list = await tareasApi.list({ mias: true });
        setMisTareas(list.filter((t) => t.estado === 'pendiente' || t.estado === 'en_curso'));
      } catch (_) { setMisTareas([]); }
      finally { setLoadingTareas(false); }
    })();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospeccion/resumen-dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setProspRes(await r.json());
      } catch (_) { /* noop */ }
    })();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const wRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/widgets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (wRes.ok) {
          const wData = await wRes.json();
          setWidgets(wData.items || []);
        }
      } catch (_) { /* noop */ }
      finally { setLoadingWidgets(false); }
    })();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/pendientes-revision`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setPendientesRev(await r.json());
      } catch (_) { /* noop */ }
    })();
    (async () => {
      try { setKpisOp(await dashboardOperativoApi.kpis()); } catch (_) { /* noop */ }
    })();
    (async () => {
      try {
        const res = await auditApi.list({ limit: 10 });
        setActividadReciente(Array.isArray(res) ? res : res.items || []);
      } catch (_) { /* noop */ }
    })();
  }, [isAdmin]);

  return {
    stats, avisos, setAvisos, loadingAvisos,
    misTareas, setMisTareas, loadingTareas,
    prospRes, widgets, loadingWidgets,
    pendientesRev, kpisOp, actividadReciente,
  };
};

// ── Secciones del dashboard ─────────────────────────────────────────
const HeroSection = ({ user, navigate }) => (
  <div className="mb-10 animate-fade-in">
    <div className="flex items-center gap-2 mb-3">
      <Badge variant="outline" className="bg-violet-50 border-violet-200 text-violet-700" data-testid="badge-fase-actual">
        <Sparkles className="w-3 h-3 mr-1" /> Centro de Inteligencia
      </Badge>
    </div>
    <h1 className="text-4xl font-heading font-bold text-ink-900 mb-3">
      Bienvenido, {user?.nombre || 'usuario'}
    </h1>
    <p className="text-lg text-ink-500 max-w-3xl">
      Tu centro de novedades. Aquí aparecerán los leads que detecta la IA, las noticias del sector, los contratos publicados y las señales de LinkedIn de tus contactos.
      <span className="text-ink-400"> Para analíticas comerciales completas, ve a <button onClick={() => navigate('/documentos-comerciales')} className="text-brand-600 hover:underline font-medium">Ventas y Métricas</button>.</span>
    </p>
  </div>
);

const MiPanelWidgets = ({ widgets, loadingWidgets }) => {
  if (widgets.length === 0 && !loadingWidgets) return null;
  return (
    <section className="mb-10" data-testid="mi-panel-widgets">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Pin className="w-4 h-4 text-violet-600" />
          <h2 className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Mi panel</h2>
        </div>
        <button
          onClick={() => { document.querySelector('[data-testid="ai-fab"]')?.click(); }}
          className="text-xs text-violet-600 hover:text-violet-700 flex items-center gap-1"
          data-testid="btn-personalizar-panel-ia"
        >
          <Sparkles className="w-3 h-3" /> Personalizar con Gemini
        </button>
      </div>
      {loadingWidgets ? (
        <div className="text-sm text-ink-400">Cargando widgets…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {widgets.map((w) => <WidgetCard key={w.id} widget={w} />)}
        </div>
      )}
    </section>
  );
};

// Iter65 — Widget de alertas de prospección territorial (admin / dir_comercial)
const ProspeccionAlertasWidget = ({ navigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    const cargar = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospeccion/fuentes/estado`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancel) setData(await r.json());
      } catch (_) { /* noop */ }
      finally { if (!cancel) setLoading(false); }
    };
    cargar();
    const t = setInterval(cargar, 60000);  // refresco cada 60s
    return () => { cancel = true; clearInterval(t); };
  }, []);

  if (loading) return null;
  const total = data?.avisos_pendientes || 0;
  const fuentesConDatos = (data?.fuentes || []).filter((f) => (f.nuevos || 0) > 0).slice(0, 3);
  const tieneActividad = total > 0 || fuentesConDatos.length > 0;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => navigate('/prospeccion/rastreo')}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate('/prospeccion/rastreo'); }}
      className="mb-10 p-5 border-ink-200 bg-white cursor-pointer hover:border-brand-300 hover:shadow-sm transition group"
      data-testid="widget-prospeccion-alertas"
    >
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${
          total > 0 ? 'bg-red-50 text-red-600' : 'bg-ink-50 text-ink-500'
        }`}>
          <Bell className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-heading font-bold text-ink-900 text-lg">Prospección territorial</h3>
            {total > 0 && (
              <Badge
                className="bg-red-500 text-white border border-red-600"
                data-testid="badge-prospeccion-avisos"
              >
                {total} {total === 1 ? 'aviso' : 'avisos'}
              </Badge>
            )}
          </div>
          <p className="text-sm text-ink-600 mb-2">
            {tieneActividad
              ? `Licitaciones nuevas detectadas en el último ciclo de las fuentes oficiales.`
              : `Sin novedades en las 4 fuentes oficiales (PLACE, APIspaín, Catalunya, Tavily+Gemini).`}
          </p>
          {fuentesConDatos.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="prospeccion-alertas-fuentes">
              {fuentesConDatos.map((f) => (
                <span key={f.nombre} className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                  {f.nombre}: <strong>{f.nuevos}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
        <ArrowRight className="w-5 h-5 text-ink-400 group-hover:text-brand-500 group-hover:translate-x-0.5 transition shrink-0 mt-1.5" />
      </div>
    </Card>
  );
};

const QuickStatsRow = ({ stats, isAdmin, user, navigate }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
    <QuickStat
      title="Clientes"
      value={stats.entidades ?? '—'}
      detail="Entidades registradas"
      icon={<Briefcase className="w-5 h-5" />}
      onClick={() => navigate('/clientes')}
      testid="stat-clientes"
      cta="Gestionar"
    />
    {isAdmin && (
      <QuickStat
        title="Equipo"
        value={stats.usuarios ?? '—'}
        detail="Usuarios en total"
        icon={<Users className="w-5 h-5" />}
        onClick={() => navigate('/usuarios')}
        testid="stat-usuarios"
        cta="Gestionar"
      />
    )}
    {isAdmin && (
      <QuickStat
        title="Audit log"
        value={stats.logs ?? '—'}
        detail="Acciones registradas"
        icon={<ScrollText className="w-5 h-5" />}
        onClick={() => navigate('/auditoria')}
        testid="stat-logs"
        cta="Ver registros"
      />
    )}
    <QuickStat
      title="Tu rol"
      value={ROL_LABEL[user?.rol] || user?.rol}
      detail={isAdmin ? 'Acceso total' : `${user?.provincias_asignadas?.length || 0} provincia${user?.provincias_asignadas?.length === 1 ? '' : 's'}`}
      icon={<Shield className="w-5 h-5" />}
      tone="ok"
      testid="stat-rol"
    />
  </div>
);

const MisTareasSection = ({ tareas, loading, onMarcarHecha, navigate }) => (
  <div className="mb-10" data-testid="mis-tareas-widget">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-xl font-heading font-bold text-ink-900 flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-brand-600" /> Mis tareas
        </h2>
        <p className="text-xs text-ink-500 mt-1">To-do pendiente que te han asignado o has creado para ti.</p>
      </div>
      <Button variant="outline" onClick={() => navigate('/tareas')} data-testid="btn-ver-tareas" className="text-sm">
        Ver todas <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
    </div>
    {loading ? (
      <Card className="p-6 text-center text-ink-400 border-ink-200 bg-white">Cargando…</Card>
    ) : tareas.length === 0 ? (
      <Card className="p-6 text-center text-ink-400 border-ink-200 bg-white" data-testid="mis-tareas-empty">
        <CheckCircle className="w-5 h-5 mx-auto mb-2 text-emerald-500" /> No tienes tareas pendientes.
      </Card>
    ) : (
      <Card className="border-ink-200 bg-white divide-y divide-ink-100" data-testid="mis-tareas-lista">
        {tareas.slice(0, 5).map((t) => {
          const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
          const venc = t.vencimiento ? new Date(t.vencimiento) : null;
          const vencida = venc && venc < hoy;
          return (
            <div key={t.id} className="p-3 flex items-start gap-3 hover:bg-ink-50">
              <button onClick={() => onMarcarHecha(t)} className="mt-0.5" title="Marcar hecha" data-testid={`btn-hecha-${t.id}`}>
                <CheckSquare className="w-5 h-5 text-ink-300 hover:text-emerald-600 transition-colors" />
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink-900 text-sm">{t.titulo}</p>
                <div className="flex items-center gap-2 text-[11px] text-ink-500 mt-0.5 flex-wrap">
                  {t.vencimiento && (
                    <span className={`inline-flex items-center gap-1 ${vencida ? 'text-amber-700 font-semibold' : ''}`}>
                      {vencida && <AlertTriangle className="w-3 h-3" />}
                      <Clock className="w-3 h-3" /> {formatFecha(t.vencimiento)}
                    </span>
                  )}
                  <Badge className="text-[10px] bg-white border border-ink-200 text-ink-600">{t.prioridad}</Badge>
                  {t.entidad_nombre && <span>· {t.entidad_nombre}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    )}
  </div>
);

const AvisosSection = ({ avisos, loading, onCompletado }) => (
  <div className="mb-10" data-testid="avisos-widget">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-xl font-heading font-bold text-ink-900 flex items-center gap-2">
          <Bell className="w-5 h-5 text-brand-600" /> Próximos avisos
        </h2>
        <p className="text-xs text-ink-500 mt-1">Fines de contrato, revisiones y licitaciones con aviso en los próximos 90 días.</p>
      </div>
      {avisos.length > 0 && (
        <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-700">
          {avisos.length} pendiente{avisos.length === 1 ? '' : 's'}
        </Badge>
      )}
    </div>
    {loading ? (
      <Card className="p-6 text-center text-ink-400 border-ink-200 bg-white">Cargando…</Card>
    ) : avisos.length === 0 ? (
      <Card className="p-6 text-center text-ink-400 border-ink-200 bg-white" data-testid="avisos-empty">
        <CheckCircle className="w-5 h-5 mx-auto mb-2 text-emerald-500" /> Sin avisos próximos. Buen trabajo.
      </Card>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="avisos-lista">
        {avisos.slice(0, 6).map((a) => <AvisoCard key={a.id} a={a} onDone={() => onCompletado(a)} />)}
      </div>
    )}
  </div>
);

const fmtEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

const BloqueKPIsOperativos = ({ kpisOp, actividad, navigate }) => {
  if (!kpisOp && (!actividad || actividad.length === 0)) return null;
  const varPct = kpisOp?.variacion_mes_pct;
  const varColor = varPct === null || varPct === undefined ? 'text-ink-400' : varPct >= 0 ? 'text-emerald-600' : 'text-red-600';

  return (
    <div className="mb-10 space-y-4">
      <h2 className="text-xl font-heading font-bold text-ink-900 flex items-center gap-2">
        <ListChecks className="w-5 h-5 text-brand-500" /> Situación operativa
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Bloque 3 — Alertas comerciales */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink-700 mb-3 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Alertas comerciales
          </h3>
          <div className="space-y-2">
            {[
              { label: 'Pedidos encallados (>200 días)', value: kpisOp?.pedidos_encallados ?? '—', href: '/documentos-comerciales', color: kpisOp?.pedidos_encallados > 0 ? 'text-red-600' : 'text-emerald-600' },
              { label: 'Albaranes sin factura (>30 días)', value: kpisOp?.albaranes_sin_factura ?? '—', href: '/documentos-comerciales', color: kpisOp?.albaranes_sin_factura > 0 ? 'text-amber-600' : 'text-emerald-600' },
              { label: 'Clientes sin actividad (>90 días)', value: kpisOp?.clientes_sin_actividad ?? '—', href: '/clientes', color: kpisOp?.clientes_sin_actividad > 0 ? 'text-amber-600' : 'text-emerald-600' },
            ].map(({ label, value, href, color }) => (
              <button key={label} onClick={() => navigate(href)} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-ink-50 border border-transparent hover:border-ink-200 transition text-left">
                <span className="text-sm text-ink-600">{label}</span>
                <span className={`text-lg font-heading font-bold ${color}`}>{value}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Bloque 4 — KPIs rápidos */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink-700 mb-3 flex items-center gap-1.5">
            <CheckSquare className="w-4 h-4 text-brand-500" /> KPIs del mes
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-brand-50">
              <span className="text-sm text-ink-600">Facturación mes actual</span>
              <div className="text-right">
                <span className="text-base font-heading font-bold text-brand-700">{fmtEur(kpisOp?.facturacion_mes_actual)}</span>
                {varPct !== null && varPct !== undefined && (
                  <span className={`ml-2 text-xs font-semibold ${varColor}`}>{varPct > 0 ? '+' : ''}{varPct}%</span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between p-2.5">
              <span className="text-sm text-ink-600">Importe pendiente de entrega</span>
              <span className="text-sm font-semibold text-ink-800">{fmtEur(kpisOp?.importe_pendiente_entrega)}</span>
            </div>
            <div className="flex items-center justify-between p-2.5">
              <span className="text-sm text-ink-600">Importe pendiente de facturar</span>
              <span className="text-sm font-semibold text-ink-800">{fmtEur(kpisOp?.importe_pendiente_facturar)}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Bloque 2 — Actividad reciente */}
      {actividad.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink-700 mb-3 flex items-center gap-1.5">
            <ScrollText className="w-4 h-4 text-ink-400" /> Actividad reciente del equipo
          </h3>
          <div className="space-y-1">
            {actividad.slice(0, 10).map((a, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5 border-b last:border-0 text-xs">
                <span className="w-32 text-ink-400 shrink-0">{a.timestamp ? new Date(a.timestamp).toLocaleString('es-ES', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                <span className="font-medium text-ink-700 truncate">{a.usuario_email || a.usuario_id || '—'}</span>
                <span className="text-ink-500 truncate">{a.accion || '—'}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

const CentrosInteligenciaSection = ({ prospRes, navigate }) => (
  <div>
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-ink-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-violet-600" /> Centro de Inteligencia IA
        </h2>
        <p className="text-sm text-ink-500 mt-1">
          Novedades que la IA encuentra por ti: nuevos leads, contratos publicados, noticias del sector y señales en LinkedIn.
          <span className="text-ink-400"> Para analíticas de ventas, ve a <button onClick={() => navigate('/documentos-comerciales')} className="text-brand-600 hover:underline font-medium">Ventas y Métricas</button>.</span>
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="centro-inteligencia">
      <CentroInteligenciaCard
        titulo="Nuevos leads detectados" descripcion="Empresas del sector detectadas por prospección territorial Tavily+Gemini"
        icon={<Users className="w-5 h-5" />} color="sky"
        estado={prospRes && prospRes.leads_total > 0 ? 'activo' : 'pronto'}
        valor={prospRes?.leads_7d ?? 0}
        leyenda={prospRes ? `${prospRes.leads_7d} últimos 7 días · ${prospRes.leads_total} totales` : 'LEADs últimos 7 días'}
        navegar={() => navigate('/clientes?es_lead=true')} testid="ci-nuevos-leads"
      />
      <CentroInteligenciaCard
        titulo="Contratos publicados" descripcion="Contratos de agua/saneamiento generados automáticamente por prospección"
        icon={<FileText className="w-5 h-5" />} color="emerald"
        estado={prospRes && prospRes.contratos_total > 0 ? 'activo' : 'pronto'}
        valor={prospRes?.contratos_30d ?? 0}
        leyenda={prospRes ? `${prospRes.contratos_30d} últimos 30 días · ${prospRes.contratos_total} totales` : 'Contratos últimos 30 días'}
        navegar={() => navigate('/contratos')} testid="ci-contratos-publicados"
      />
    </div>

    {prospRes && prospRes.ultimos_leads && prospRes.ultimos_leads.length > 0 && (
      <Card className="mt-4 p-5 border-sky-200" data-testid="ultimos-leads-prospeccion">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink-800 flex items-center gap-2">
            <Users className="w-4 h-4 text-sky-600" /> Últimos LEADs detectados
          </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/clientes?es_lead=true')} className="text-xs text-sky-700 hover:bg-sky-50" data-testid="ver-todos-leads">
            Ver todos ({prospRes.leads_total}) <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        <div className="space-y-2">
          {prospRes.ultimos_leads.map((l) => (
            <button key={l.id} onClick={() => navigate(`/clientes/${l.id}`)} className="w-full text-left px-3 py-2 hover:bg-ink-50 rounded flex items-center gap-3 border border-transparent hover:border-ink-200" data-testid={`lead-${l.id}`}>
              <div className="h-8 w-8 rounded bg-ink-100 overflow-hidden shrink-0">
                {l.logo_url && <img src={l.logo_url} alt="" className="h-full w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink-800 truncate">
                  {l.razon_social}
                  {l.es_ute && <Badge className="ml-2 bg-amber-100 border-amber-300 text-amber-800 border text-[9px]">UTE</Badge>}
                </div>
                <div className="text-xs text-ink-500 truncate">{l.provincia || '—'} · {l.origen || '—'}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-ink-400 shrink-0" />
            </button>
          ))}
        </div>
      </Card>
    )}
  </div>
);

const PendientesRevisionWidget = ({ data, navigate }) => {
  if (!data || data.total === 0) return null;
  return (
    <Card
      className="mb-10 p-5 border-amber-200 bg-gradient-to-br from-amber-50/80 to-white"
      data-testid="widget-pendientes-revision"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-11 h-11 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-ink-900 text-lg mb-1">
            {data.total} registro{data.total === 1 ? '' : 's'} pendiente{data.total === 1 ? '' : 's'} de revisión
          </h3>
          <p className="text-sm text-ink-600">
            Datos creados por importación, prospección o enriquecimiento que aún no han sido validados manualmente.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          onClick={() => navigate('/clientes?pendiente_revision=true')}
          disabled={data.entidades === 0}
          className="text-left p-4 border border-amber-200 rounded-lg bg-white hover:bg-amber-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="link-pendientes-entidades"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <Inbox className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-heading font-bold text-ink-900">{data.entidades}</p>
              <p className="text-xs text-ink-500">empresa{data.entidades === 1 ? '' : 's'} pendiente{data.entidades === 1 ? '' : 's'}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-600 shrink-0" />
          </div>
        </button>
        <button
          onClick={() => navigate('/contactos?pendiente_revision=true')}
          disabled={data.contactos === 0}
          className="text-left p-4 border border-amber-200 rounded-lg bg-white hover:bg-amber-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="link-pendientes-contactos"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <UserCog className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-heading font-bold text-ink-900">{data.contactos}</p>
              <p className="text-xs text-ink-500">contacto{data.contactos === 1 ? '' : 's'} pendiente{data.contactos === 1 ? '' : 's'}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-600 shrink-0" />
          </div>
        </button>
      </div>
    </Card>
  );
};

// ── Página principal ────────────────────────────────────────────────
const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const data = useDashboardData(isAdmin);

  const marcarHecha = async (t) => {
    try {
      await tareasApi.update(t.id, { estado: 'hecha' });
      data.setMisTareas(data.misTareas.filter((x) => x.id !== t.id));
    } catch (e) { alert(e.message); }
  };

  const marcarCompletado = async (a) => {
    try {
      await avisosApi.update(a.id, { completado: true });
      data.setAvisos(data.avisos.filter((x) => x.id !== a.id));
    } catch (e) { alert(e.message); }
  };

  return (
    <div data-testid="dashboard">
      <HeroSection user={user} navigate={navigate} />
      <MiPanelWidgets widgets={data.widgets} loadingWidgets={data.loadingWidgets} />
      <QuickStatsRow stats={data.stats} isAdmin={isAdmin} user={user} navigate={navigate} />
      {(user?.rol === 'admin' || user?.rol === 'dir_comercial') && (
        <ProspeccionAlertasWidget navigate={navigate} />
      )}
      <PendientesRevisionWidget data={data.pendientesRev} navigate={navigate} />
      <BloqueKPIsOperativos kpisOp={data.kpisOp} actividad={data.actividadReciente} navigate={navigate} />

      {isAdmin && (
        <Card className="mb-10 p-5 border-brand-200 bg-gradient-to-br from-brand-50/80 to-white" data-testid="docs-internos-card">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-11 h-11 rounded-lg bg-brand-500 text-white flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-ink-900 text-lg mb-1">Documentación técnica del proyecto</h3>
              <p className="text-sm text-ink-600">
                Hoja de ruta v3 consolidada + Plan Maestro + Addendums (Correo · ERP Sync) + Trabajo pendiente histórico.
              </p>
            </div>
          </div>
          <DocsInternos />
        </Card>
      )}

      <MisTareasSection tareas={data.misTareas} loading={data.loadingTareas} onMarcarHecha={marcarHecha} navigate={navigate} />
      <AvisosSection avisos={data.avisos} loading={data.loadingAvisos} onCompletado={marcarCompletado} />
      <CentrosInteligenciaSection prospRes={data.prospRes} navigate={navigate} />
    </div>
  );
};

export default Dashboard;
